// Bank-statement reconciliation for the driver excess fund. Janelle sends
// each driver's weekly contribution into a separate bank account; this
// matches an uploaded CSV of that account's transactions against the
// driver_excess_fund_ledger "contribution" rows payroll already logged
// (via recordWeeklyFundContributions in incidentReports.ts), so she can
// confirm what actually landed against what was expected -- one bank
// transaction per driver per week, matched mostly automatically.
import { supabase } from "@/lib/supabase";
import { recordExcessFundLedgerEntry } from "@/lib/incidentReports";

export interface ParsedBankRow {
  date: string; // YYYY-MM-DD, best-effort parsed from whatever the bank exported
  amount: number;
  reference: string;
}

/** Minimal but real CSV parser (quoted fields, embedded commas/quotes) --
 *  bank exports routinely quote the particulars/reference field, which a
 *  naive split(",") breaks on. */
export function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      pushField();
    } else if (c === "\n") {
      pushRow();
    } else if (c === "\r") {
      // skip -- \r\n line endings
    } else {
      field += c;
    }
  }
  if (field !== "" || row.length > 0) pushRow();

  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

/** Best-effort date normaliser -- handles DD/MM/YYYY (most NZ bank exports),
 *  YYYY-MM-DD (already ISO), and DD-MM-YYYY. Returns null if it can't tell. */
export function normalizeDate(raw: string): string | null {
  const s = raw.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (iso) return s;
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return null;
}

/** Parses a bank-account-style amount ($, commas, parentheses/minus for
 *  debits) into a plain positive/negative number. */
export function parseAmount(raw: string): number | null {
  const s = raw.trim().replace(/[$,]/g, "");
  if (!s) return null;
  const negative = /^\(.*\)$/.test(s);
  const n = parseFloat(negative ? s.slice(1, -1) : s);
  if (Number.isNaN(n)) return null;
  return negative ? -n : n;
}

const HEADER_HINTS = {
  date: ["date", "transaction date", "value date", "processed date"],
  amount: ["amount", "credit", "credit amount", "deposit"],
  reference: ["reference", "particulars", "description", "narrative", "details", "payee"],
};

/** Guesses which column index is which from the header row, so the upload
 *  step is usually a single click instead of manual mapping every time. */
export function guessColumns(header: string[]): { date: number; amount: number; reference: number } {
  const norm = header.map((h) => h.trim().toLowerCase());
  const find = (hints: string[]) => {
    for (const hint of hints) {
      const idx = norm.findIndex((h) => h === hint);
      if (idx !== -1) return idx;
    }
    for (const hint of hints) {
      const idx = norm.findIndex((h) => h.includes(hint));
      if (idx !== -1) return idx;
    }
    return -1;
  };
  return {
    date: find(HEADER_HINTS.date),
    amount: find(HEADER_HINTS.amount),
    reference: find(HEADER_HINTS.reference),
  };
}

const normalizeText = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Bank-statement housekeeping rows that never represent a driver
 *  contribution -- auto-ignored so Janelle doesn't have to hand-dismiss an
 *  opening/closing balance line and a credits line every single week. */
const ADMINISTRATIVE_REFERENCES = new Set(["opening balance", "closing balance", "credit", "credits"]);

export function isAdministrativeReference(reference: string): boolean {
  return ADMINISTRATIVE_REFERENCES.has(normalizeText(reference));
}

/** Suggests a driver for a bank row's reference text -- full-name match
 *  first, then last-name-only as a fallback (references are often
 *  abbreviated or truncated by the bank). */
export function suggestDriverForReference(reference: string, driverNames: string[]): string | null {
  const norm = normalizeText(reference);
  if (!norm) return null;
  for (const name of driverNames) {
    if (normalizeText(name) && norm.includes(normalizeText(name))) return name;
  }
  for (const name of driverNames) {
    const parts = name.trim().split(/\s+/);
    const last = parts[parts.length - 1];
    if (last.length >= 3 && norm.includes(normalizeText(last))) return name;
  }
  return null;
}

export interface UnreconciledContribution {
  id: string;
  driver_name: string;
  amount: number;
  created_at: string;
}

/** Every logged contribution not yet matched to a real bank transaction,
 *  oldest first so FIFO matching picks the earliest outstanding one. */
export async function fetchUnreconciledContributions(): Promise<UnreconciledContribution[]> {
  const { data, error } = await supabase
    .from("driver_excess_fund_ledger")
    .select("id, driver_name, amount, created_at")
    .eq("entry_type", "contribution")
    .eq("bank_reconciled", false)
    .order("created_at", { ascending: true });
  if (error) {
    console.error("Error loading unreconciled contributions:", error);
    return [];
  }
  return data || [];
}

/** A bank-confirmed payment predates the driver's own insurance_start_date
 *  (or they have none set at all) -- move the tenure clock back to the
 *  earliest date we now have real proof for, so "Contributing Since" and
 *  the claimable-tier math reflect their actual history instead of
 *  whichever date someone happened to click "Start Contributing". Never
 *  moves it forward -- only ever earlier. */
async function backfillInsuranceStartDate(driverName: string, date: string): Promise<void> {
  const { data } = await supabase.from("drivers").select("insurance_start_date").eq("driver_name", driverName).maybeSingle();
  const current = data?.insurance_start_date as string | null | undefined;
  if (!current || date < current) {
    await supabase.from("drivers").update({ insurance_start_date: date }).eq("driver_name", driverName);
  }
}

export type ReconciliationStatus = "matched" | "unmatched" | "ignored";

export interface ReconciliationRow {
  date: string;
  amount: number;
  reference: string;
  matchedDriverName: string | null;
  matchedLedgerEntryId: string | null;
  status: ReconciliationStatus;
}

/** Picks the best outstanding ledger entry for a driver -- prefers an exact
 *  amount match, falls back to their oldest outstanding entry either way,
 *  and never reuses an entry another row in this same batch already claimed. */
export function pickLedgerEntryForDriver(
  driverName: string,
  amount: number,
  unreconciled: UnreconciledContribution[],
  alreadyClaimedIds: Set<string>
): UnreconciledContribution | null {
  const candidates = unreconciled.filter((e) => e.driver_name === driverName && !alreadyClaimedIds.has(e.id));
  if (candidates.length === 0) return null;
  const exact = candidates.find((e) => Math.abs(e.amount - amount) < 0.005);
  return exact || candidates[0];
}

/** Builds the initial review table from parsed rows -- auto-suggests a
 *  driver + ledger entry per row where possible, leaving the rest for
 *  manual matching. Never double-books the same ledger entry across rows. */
export function buildReconciliationRows(
  parsed: ParsedBankRow[],
  driverNames: string[],
  unreconciled: UnreconciledContribution[]
): ReconciliationRow[] {
  const claimed = new Set<string>();
  return parsed.map((row) => {
    if (isAdministrativeReference(row.reference)) {
      return { ...row, matchedDriverName: null, matchedLedgerEntryId: null, status: "ignored" as const };
    }
    const driverName = suggestDriverForReference(row.reference, driverNames);
    if (!driverName) {
      return { ...row, matchedDriverName: null, matchedLedgerEntryId: null, status: "unmatched" as const };
    }
    // Identifying the driver is what "matched" means here -- an outstanding
    // ledger entry to link is a bonus (it also flips bank_reconciled on
    // save), not a requirement. Bank statements routinely predate when
    // recordWeeklyFundContributions started logging ledger rows at all, so
    // plenty of correctly-identified historical rows will never have one.
    const entry = pickLedgerEntryForDriver(driverName, row.amount, unreconciled, claimed);
    if (entry) claimed.add(entry.id);
    return { ...row, matchedDriverName: driverName, matchedLedgerEntryId: entry?.id || null, status: "matched" as const };
  });
}

/** Saves every reviewed row as a bank_transactions record (full audit trail
 *  of what was uploaded, matched or not). A matched row either flips
 *  bank_reconciled on the ledger entry it linked to, or -- when no entry
 *  existed to link (the norm for anything predating ledger tracking, e.g.
 *  a first historical upload) -- creates the contribution entry directly
 *  from the bank data itself, backdated to the real transaction date, and
 *  backfills the driver's insurance_start_date if this is now their
 *  earliest known payment. Otherwise a bank-confirmed payment would just
 *  sit labelled with a name and never actually show up anywhere on the
 *  driver's fund record. */
export async function saveReconciliation(rows: ReconciliationRow[]): Promise<{ matched: number; unmatched: number; ignored: number }> {
  const uploadBatchId = crypto.randomUUID();
  let matched = 0;
  let unmatched = 0;
  let ignored = 0;

  for (const r of rows) {
    let ledgerEntryId = r.matchedLedgerEntryId;

    if (r.status === "matched" && r.matchedDriverName) {
      if (ledgerEntryId) {
        const { error } = await supabase
          .from("driver_excess_fund_ledger")
          .update({ bank_reconciled: true })
          .eq("id", ledgerEntryId);
        if (error) throw error;
      } else {
        ledgerEntryId = await recordExcessFundLedgerEntry({
          driver_name: r.matchedDriverName,
          entry_type: "contribution",
          amount: r.amount,
          note: `Bank reconciliation -- ${r.reference}`,
          entryDate: r.date,
          bank_reconciled: true,
        });
      }
      await backfillInsuranceStartDate(r.matchedDriverName, r.date);
    }

    if (r.status === "matched") matched++;
    else if (r.status === "ignored") ignored++;
    else unmatched++;

    const { error: insertError } = await supabase.from("insurance_excess_bank_transactions").insert({
      transaction_date: r.date,
      amount: r.amount,
      reference: r.reference,
      matched_driver_name: r.matchedDriverName,
      matched_ledger_entry_id: ledgerEntryId,
      status: r.status,
      upload_batch_id: uploadBatchId,
    });
    if (insertError) throw insertError;
  }

  return { matched, unmatched, ignored };
}

export interface BankTransactionRow {
  id: string;
  transaction_date: string;
  amount: number;
  reference: string | null;
  matched_driver_name: string | null;
  status: ReconciliationStatus;
  uploaded_at: string;
}

/** Re-matches an already-saved bank transaction (e.g. one left unmatched on
 *  first upload) -- same "identifying the driver is enough" rule as
 *  buildReconciliationRows: tries to link an existing outstanding ledger
 *  entry, and if there isn't one, creates the contribution entry directly
 *  from this transaction (backdated, backfilling insurance_start_date too)
 *  rather than leaving the match as a name with nothing behind it. */
export async function updateBankTransactionMatch(
  transaction: BankTransactionRow,
  driverName: string | null,
  status: ReconciliationStatus
): Promise<void> {
  let matchedLedgerEntryId: string | null = null;
  if (driverName) {
    const unreconciled = await fetchUnreconciledContributions();
    const entry = pickLedgerEntryForDriver(driverName, transaction.amount, unreconciled, new Set());
    if (entry) {
      matchedLedgerEntryId = entry.id;
      const { error: ledgerError } = await supabase
        .from("driver_excess_fund_ledger")
        .update({ bank_reconciled: true })
        .eq("id", matchedLedgerEntryId);
      if (ledgerError) throw ledgerError;
    } else {
      matchedLedgerEntryId = await recordExcessFundLedgerEntry({
        driver_name: driverName,
        entry_type: "contribution",
        amount: transaction.amount,
        note: `Bank reconciliation -- ${transaction.reference || ""}`,
        entryDate: transaction.transaction_date,
        bank_reconciled: true,
      });
    }
    await backfillInsuranceStartDate(driverName, transaction.transaction_date);
  }

  const { error } = await supabase
    .from("insurance_excess_bank_transactions")
    .update({ matched_driver_name: driverName, matched_ledger_entry_id: matchedLedgerEntryId, status })
    .eq("id", transaction.id);
  if (error) throw error;
}

/** Filtered by status server-side, not just the most-recent handful --
 *  uploads run into the hundreds of rows per statement, and the whole point
 *  of the Unmatched tab is to see every one still needing attention, not
 *  just whichever happened to be in the last 50 uploaded. */
export async function fetchRecentBankTransactions(status: ReconciliationStatus, limit = 500): Promise<BankTransactionRow[]> {
  const { data, error } = await supabase
    .from("insurance_excess_bank_transactions")
    .select("id, transaction_date, amount, reference, matched_driver_name, status, uploaded_at")
    .eq("status", status)
    .order("transaction_date", { ascending: false })
    .limit(limit);
  if (error) {
    console.error("Error loading recent bank transactions:", error);
    return [];
  }
  return data || [];
}

export async function fetchBankTransactionStatusCounts(): Promise<Record<ReconciliationStatus, number>> {
  const statuses: ReconciliationStatus[] = ["matched", "unmatched", "ignored"];
  const results = await Promise.all(
    statuses.map((s) =>
      supabase.from("insurance_excess_bank_transactions").select("id", { count: "exact", head: true }).eq("status", s)
    )
  );
  const counts = { matched: 0, unmatched: 0, ignored: 0 } as Record<ReconciliationStatus, number>;
  statuses.forEach((s, i) => {
    counts[s] = results[i].count || 0;
  });
  return counts;
}
